import React, { useState, useEffect } from 'react';
import {
    useDataProvider,
    Loading,
    Error,
} from 'react-admin';
import { getCashbackFactor, getChainId } from '../lib';
import { makeStyles, Typography } from '@material-ui/core';
import ProductGrid from './ProductGrid'
import ProductsToolbar from './ProductsToolbar'
import { PLACE_ID_KEY } from '../constants';
import { cashValueOf } from '../utils/cashProduct';

const SEARCH_DEBOUNCE_MS = 300;

const useStyles = makeStyles(theme => ({
    sectionHeader: {
        display: 'flex',
        alignItems: 'baseline',
        marginTop: 28,
    },
    sectionTitle: {
        fontSize: 18,
        fontWeight: 600,
        marginRight: 8,
    },
    sectionCount: {
        fontSize: 13,
        color: theme.palette.text.secondary,
    },
}));

const SectionHeader = ({ title, count, classes }) => (
    <div className={classes.sectionHeader}>
        <Typography component="h2" className={classes.sectionTitle}>{title}</Typography>
        {count > 0 && <span className={classes.sectionCount}>{count}</span>}
    </div>
);

const ProductList = ({ productSelectedCallback, cashbackSelectedCallback }) => {
    const classes = useStyles();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState();
    const [term, setTerm] = useState('');
    const [search, setSearch] = useState('');
    const [onlyMoneyProducts, setOnlyMoneyProducts] = useState(false);
    const [productsList, setProductsList] = useState();
    const [featuredProductsList, setFeaturedProductsList] = useState();
    const dataProvider = useDataProvider();
    const cashbackFactor = getCashbackFactor();

    // Sem o debounce, cada tecla digitada disparava dois requests (produtos +
    // destaques) contra a API.
    useEffect(() => {
        const timer = setTimeout(() => { setSearch(term); }, SEARCH_DEBOUNCE_MS);
        return () => { clearTimeout(timer); };
    }, [term]);

    useEffect(() => {
        const makeProductsRequests = async () => {
            const filter = { search };
            filter.isCash = onlyMoneyProducts;
            const placeId = localStorage.getItem(PLACE_ID_KEY);
            if (placeId) {
                filter.placeId = placeId;
            }

            const pagination = { page: 1, perPage: 50000 };
            const sort = { field: 'name', order: 'ASC' };

            Promise.all([
                dataProvider.getList(`chains/${getChainId()}/featured-products`, { pagination, sort, filter }),
                dataProvider.getList(`chains/${getChainId()}/products`, { pagination, sort, filter }),
            ])
                .then(function ([featuredProducts, products]) {
                    setFeaturedProductsList(featuredProducts.data.map((featuredProduct) => featuredProduct.product));
                    if (onlyMoneyProducts) {
                        // Antes o name era sobrescrito pelo número só para poder ordenar —
                        // com cash_value o nome é nome de verdade e não pode ser destruído.
                        setProductsList([...products.data].sort((a, b) => {
                            const valueA = cashValueOf(a);
                            const valueB = cashValueOf(b);
                            if (valueA === null) { return 1; }
                            if (valueB === null) { return -1; }
                            return valueA - valueB;
                        }))
                    } else { setProductsList(products.data); }
                    setLoading(false);
                })
                .catch(function (err) {
                    setError(err);
                    setLoading(false);
                })
        }
        makeProductsRequests();
    }, [search, onlyMoneyProducts, dataProvider]);

    if (error) return <Error />;

    const featuredCount = featuredProductsList ? featuredProductsList.length : 0;
    const productsCount = productsList ? productsList.length : 0;
    const emptyMessage = search
        ? `Nenhum produto encontrado para "${search}"`
        : 'Nenhum produto disponível neste posto';

    return (
        <>
            <ProductsToolbar
                term={term}
                onTermChange={setTerm}
                onlyMoneyProducts={onlyMoneyProducts}
                onOnlyMoneyProductsChange={setOnlyMoneyProducts}
                cashbackFactor={cashbackFactor}
                onCashbackClick={cashbackSelectedCallback}
            />

            {loading ? <Loading /> : (
                <>
                    {featuredCount > 0 && (
                        <>
                            <SectionHeader title="Produtos em destaque" count={featuredCount} classes={classes} />
                            <ProductGrid data={featuredProductsList} productSelectedCallback={productSelectedCallback} />
                        </>
                    )}
                    <SectionHeader title="Todos os produtos" count={productsCount} classes={classes} />
                    <ProductGrid
                        data={productsList}
                        productSelectedCallback={productSelectedCallback}
                        emptyMessage={emptyMessage}
                    />
                </>
            )}
        </>
    );
}

export default ProductList;
